import {
  createEntityAdapter,
  createSelector,
  createSlice,
  nanoid,
  type EntityState,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  extractWidgetDefaults,
  widgetRegistry,
  type WidgetId,
  type WidgetInstance,
  type WidgetType,
} from '../widgetManager';

import { blockSlice } from './block.slice';
import { sectionSlice } from './section.slice';

import type { RootState } from '../redux-store';

export const adapter = createEntityAdapter<WidgetInstance, WidgetId>({
  selectId: w => w.id,
});

export const widgetInstanceSlice = createSlice({
  name: 'layout/widgetInstances',
  initialState: adapter.getInitialState(),
  reducers: {
    setWidgetInstance: {
      prepare(payload: Partial<WidgetInstance>) {
        // Pass through; no defaults here
        return { payload };
      },
      reducer: (state, action: PayloadAction<Partial<WidgetInstance>>) => {
        const { id, type, settings, title, sectionId } = action.payload;
        const existing = id && state.entities[id];
        if (id && existing) {
          // Shared-settings update → update all shared instances of this type
          if (existing.settings?.shareSettings && settings) {
            const instancesOfType = Object.values(state.entities).filter(
              i => i?.type === type && i?.settings?.shareSettings,
            ) as WidgetInstance[];

            instancesOfType.forEach(inst => {
              adapter.updateOne(state, {
                id: inst.id,
                changes: {
                  // keep title/sectionId as-is for each instance
                  settings: { ...(inst.settings ?? {}), ...(settings ?? {}) },
                },
              });
            });
          } else {
            // Regular single-instance merge
            adapter.updateOne(state, {
              id,
              changes: {
                // keep title/sectionId if provided
                ...(title !== undefined ? { title } : null),
                ...(sectionId !== undefined ? { sectionId } : null),
                settings: { ...(existing.settings ?? {}), ...(settings ?? {}) },
              },
            });
          }
        } else {
          if (!type) {
            throw new Error(
              'Cannot create new widget instance without type specified',
            );
          }
          const cfg = widgetRegistry.get(type)?.config;
          if (!cfg) {
            throw new Error(`Unknown widget type: ${String(type)}`);
          }
          const defaults = extractWidgetDefaults(cfg);

          // New instance → apply defaults once
          adapter.addOne(state, {
            id: id || `${type}-${nanoid().toString()}`,
            type,
            title: title || widgetRegistry.get(type)?.name || 'Untitled Widget',
            sectionId: sectionId !== undefined ? sectionId : null,
            settings: { ...defaults, ...(settings ?? {}) },
          });
        }
      },
    },
    setWidgetInstanceVisible(
      state,
      { payload }: PayloadAction<{ id: WidgetId; visible: boolean }>,
    ) {
      const inst = state.entities[payload.id];
      if (!inst) {
        return;
      }
      inst.visible = payload.visible;
    },
    removeWidgetInstance: (state, action: PayloadAction<WidgetInstance>) => {
      adapter.removeOne(state, action.payload.id);
    },
    setWidgetInstanceSealed(
      state,
      { payload }: PayloadAction<{ id: WidgetId; sealed: boolean }>,
    ) {
      const inst = state.entities[payload.id];
      if (!inst) {
        return;
      }
      inst.sealed = payload.sealed;
    },

    /**
     * Replace entire widget instances state (used for template application)
     */
    setWidgetInstances(
      state,
      action: PayloadAction<Record<WidgetId, WidgetInstance>>,
    ) {
      // `visible` is a runtime flag set by Widget mount/unmount — override template value to keep it in sync
      const instancesArray = Object.values(action.payload).map(instance => ({
        ...instance,
        visible: state.entities[instance.id]?.visible ?? false,
      }));
      adapter.setAll(state, instancesArray);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(blockSlice.actions.blockRemove, (state, action) => {
        // Find and remove the widget instances associated with the block
        const blockId = action.payload;

        adapter.removeOne(state, blockId);
      })
      .addCase(
        sectionSlice.actions.sectionSetBlocks,
        (state, { payload: { blockIds, sectionId } }) => {
          Object.values(state.entities).forEach(b => {
            if (blockIds.includes(b.id)) {
              adapter.updateOne(state, { id: b.id, changes: { sectionId } });
            } else if (b.sectionId === sectionId) {
              adapter.updateOne(state, {
                id: b.id,
                changes: { sectionId: null },
              });
            }
          });
        },
      );
  },
});

export const widgetInstanceReducer = widgetInstanceSlice.reducer;
export const widgetInstanceActions = widgetInstanceSlice.actions;

type WidgetInstancesState = EntityState<WidgetInstance, WidgetId>;
const selectors = adapter.getSelectors<{
  widgetInstances: WidgetInstancesState;
}>(s => s.widgetInstances);

const selectVisibleWidgetInstancesByType = <T extends WidgetType>(type?: T) =>
  createSelector(
    [(state: RootState) => selectors.selectAll(state)],
    allInstances =>
      allInstances.filter(i => i.type === type && i.visible === true),
  );

export const selectWidgetSettingsWithSharing = <T extends WidgetType>(
  instance: WidgetInstance<T> | undefined,
) =>
  createSelector(
    [(state: RootState) => selectors.selectAll(state)],
    allInstances => {
      if (!instance) {
        return undefined;
      }

      if (instance.settings?.shareSettings) {
        const sharedInstances = allInstances.filter(
          i => i.type === instance.type && i.settings?.shareSettings,
        );

        if (sharedInstances.length > 0) {
          return sharedInstances[0].settings;
        }
      }
      return instance.settings;
    },
  );

export const useSelectWidgetInstanceById = (id: WidgetId) =>
  useSelector((state: RootState) => selectors.selectById(state, id));
export const useSelectWidgetInstanceIds = () =>
  useSelector((state: RootState) => selectors.selectIds(state));
export const useSelectWidgetInstanceEntities = () =>
  useSelector((state: RootState) => selectors.selectEntities(state));
export const useSelectWidgetInstanceAll = () =>
  useSelector((state: RootState) => selectors.selectAll(state));
export const useSelectWidgetInstanceTotal = () =>
  useSelector((state: RootState) => selectors.selectTotal(state));
export const useSelectVisibleWidgetInstancesByType = <T extends WidgetType>(
  type: T,
) => {
  const visibleWidgetInstancesByTypeSelector = useMemo(
    () => selectVisibleWidgetInstancesByType(type),
    [type],
  );
  return useSelector(visibleWidgetInstancesByTypeSelector);
};
export const useSelectWidgetSettingsWithSharing = <T extends WidgetType>(
  instance: WidgetInstance<T> | undefined,
) => {
  const visibleWidgetInstancesByTypeSelector = useMemo(
    () => selectWidgetSettingsWithSharing(instance),
    [instance],
  );
  return useSelector(visibleWidgetInstancesByTypeSelector);
};
